import { faLocationArrow, faMapPin } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { hasValue, LocationTracker, styles } from "@repo/helpers";
import { Button } from "@repo/ui";
import GoogleMapReact, { MapOptions } from "google-map-react";
import { useEffect, useRef, useState } from "react";
import { createRoot } from "react-dom/client";

import style from "./style.module.scss";
import { ControlPosition, MarkerProps, Props } from "./types";

const _geographicCenterOfUS = {
	lat: 39.8283,
	lng: -98.5795,
} as GoogleMapReact.Coords;

const _resolveFunctionName = "setCurrentLocationMarkerCallback";

const _topPositions = [ControlPosition.TOP_LEFT, ControlPosition.TOP_CENTER, ControlPosition.TOP_RIGHT];
const _bottomPositions = [ControlPosition.BOTTOM_LEFT, ControlPosition.BOTTOM_CENTER, ControlPosition.BOTTOM_RIGHT];
const _leftPositions = [ControlPosition.TOP_LEFT, ControlPosition.CENTER_LEFT, ControlPosition.BOTTOM_LEFT];
const _rightPositions = [ControlPosition.TOP_RIGHT, ControlPosition.CENTER_RIGHT, ControlPosition.BOTTOM_RIGHT];

export default function GoogleMap(props: Props) {
	const {
		locationTracker: locationTrackerProp,
		center: centerProp,
		markers,
		initialZoomLevel,
		centerOnCurrentLocation,
		showCurrentLocationMarker,
		accuracyCircleRef,
		markersToCenterOn,
		apiKey,
		children,
		customControlButtons,
		setMapRef,
		...passProps
	} = props;

	const { options } = passProps;

	const locationTracker = useRef(locationTrackerProp ?? new LocationTracker());
	const currentLocationMarker = useRef<any>(null);

	const [center, setCenter] = useState<GoogleMapReact.Coords>(_geographicCenterOfUS);
	const [zoom, setZoom] = useState<number>(initialZoomLevel);

	const [maps, setMaps] = useState<any>(null);
	const [map, setMap] = useState<any>(null);

	useEffect(() => {
		return () => locationTracker.current.removeResolveFunction(_resolveFunctionName);
	}, []);

	useEffect(() => {
		centerProp && setCenter(centerProp);
	}, [centerProp]);

	useEffect(() => {
		if (hasValue(map) && hasValue(maps)) {
			initializeMap();
		}
	}, [map, maps]);

	async function initializeMap() {
		try {
			const currentLocation = await initializeCenterOfMap();
			initializeMarkers(currentLocation.coords);
		} catch (err) {
			// center of US
			initializeMarkers({
				latitude: _geographicCenterOfUS.lat,
				longitude: _geographicCenterOfUS.lng,
			} as GeolocationCoordinates);
		}
	}

	async function initializeCenterOfMap() {
		await locationTracker.current.continueTracking();
		const currentLocation = locationTracker.current.mostRecentPosition();

		let newCenter = null as GoogleMapReact.Coords;
		if (centerOnCurrentLocation)
			newCenter = {
				lat: currentLocation.coords.latitude,
				lng: currentLocation.coords.longitude,
			};
		else if (centerProp) newCenter = { lat: centerProp.lat, lng: centerProp.lng };
		else newCenter = _geographicCenterOfUS;

		setCenter(newCenter);

		return currentLocation;
	}

	function initializeMarkers(coords: GeolocationCoordinates) {
		setCurrentLocationMarker(coords);
		locationTracker.current.addResolveFunction(_resolveFunctionName, setCurrentLocationMarker);
	}

	function setCurrentLocationMarker(coords: GeolocationCoordinates) {
		if (showCurrentLocationMarker) {
			const latitude = coords.latitude;
			const longitude = coords.longitude;
			const accuracy = coords.accuracy;
			const centerPosition = new maps.LatLng(latitude, longitude);

			if (hasValue(currentLocationMarker.current)) currentLocationMarker.current.setMap(null);

			currentLocationMarker.current = new maps.Marker({
				position: centerPosition,
				map: map,
				icon: {
					path: "M512 256C512 397.4 397.4 512 256 512C114.6 512 0 397.4 0 256C0 114.6 114.6 0 256 0C397.4 0 512 114.6 512 256z",
					fillColor: "blue",
					fillOpacity: 0.65,
					strokeWeight: 3,
					strokeColor: "white",
					strokeOpacity: 0.8,
					rotation: 0,
					scale: 0.03,
					anchor: new maps.Point(250, 250),
				},
			});

			if (hasValue(accuracyCircleRef.current)) accuracyCircleRef.current.setMap(null);

			accuracyCircleRef.current = new maps.Circle({
				center: centerPosition,
				radius: accuracy,
				map: map,
				fillColor: "blue",
				fillOpacity: coords.accuracy > 100 ? 0 : 0.2,
				strokeColor: "darkblue",
				strokeOpacity: 0.5,
			});
		}
	}

	function renderMarkers() {
		return markers?.map((m, i) => <Marker key={i} {...m} />);
	}

	function centerOnLocation() {
		const currentLocation = locationTracker.current.mostRecentPosition();
		setCenter({
			lat: currentLocation.coords.latitude,
			lng: currentLocation.coords.longitude,
		});
		setZoom(GoogleMap.defaultProps.initialZoomLevel);
	}

	function centerOnMarker(markerName: string) {
		const markerToCenterOn = markers?.find((m) => m.name === markerName);
		if (hasValue(markerToCenterOn)) {
			setCenter({ lat: markerToCenterOn.lat, lng: markerToCenterOn.lng });
			setZoom(GoogleMap.defaultProps.initialZoomLevel);
		}
	}

	function handleApiLoaded(map, maps, ref: Element) {
		const centerOnLocationButton = (
			<Button onClick={centerOnLocation}>
				{<FontAwesomeIcon icon={faLocationArrow} color={"blue"} size={"2x"} />}
			</Button>
		);
		const markersToCenterOnButtons = (markersToCenterOn ?? []).map((m) => (
			<Button key={m} onClick={() => centerOnMarker(m)}>
				{<FontAwesomeIcon icon={faMapPin} color={"red"} size={"2x"} />}
			</Button>
		));

		for (const value in ControlPosition) {
			const location = value as ControlPosition;

			const div = document.createElement("div");
			div.classList.add(style.customMapControlButtonsDiv);

			if (_topPositions.includes(location)) div.classList.add(style.topButton);
			if (_bottomPositions.includes(location)) div.classList.add(style.bottomButton);
			if (_leftPositions.includes(location)) div.classList.add(style.leftButton);
			if (_rightPositions.includes(location)) div.classList.add(style.rightButton);

			const customButtons = (customControlButtons ?? [])
				.filter((c) => c.location === location)
				.map((c, i) => (
					<Button key={i} onClick={() => c.onClick(ref)}>
						{<FontAwesomeIcon icon={c.icon} color={c.color} size={"2x"} />}
					</Button>
				));

			const buttonsToRender =
				location === ControlPosition.BOTTOM_RIGHT
					? [
							...customButtons,
							...markersToCenterOnButtons,
							showCurrentLocationMarker && centerOnLocationButton,
						]
					: customButtons;

			if (hasValue(buttonsToRender) && buttonsToRender.length > 0) {
				const root = createRoot(div);
				root.render(buttonsToRender);
				map.controls[maps.ControlPosition[location]].push(div);
			}
		}

		setMap(map);
		setMaps(maps);
		setMapRef && setMapRef(ref as HTMLElement);
		return null;
	}

	function mapOnChange({ center, zoom }) {
		setCenter(center);
		setZoom(zoom);
	}

	return (
		<GoogleMapReact
			{...passProps}
			center={center}
			zoom={zoom}
			options={options ?? defaultMapOptions}
			onChange={mapOnChange}
			bootstrapURLKeys={{ key: apiKey }}
			yesIWantToUseGoogleMapApiInternals
			onGoogleApiLoaded={({ map, maps, ref }) => handleApiLoaded(map, maps, ref)}
		>
			{renderMarkers()}
			{children}
		</GoogleMapReact>
	);
}

GoogleMap.defaultProps = {
	apiKey: "AIzaSyDcKVNzjWpSbwSvi8lig6-b5m9iXn7I1zI",
	initialZoomLevel: 20,
	showCurrentLocationMarker: false,
	centerOnCurrentLocation: false,
} as Props;

const defaultMapOptions = {
	mapTypeControl: true,
	streetViewControl: true,
} as MapOptions;

function Marker(props: MarkerProps) {
	const { icon, color, size, className, text } = props;
	return (
		<div className={styles(style.markers, className)}>
			<FontAwesomeIcon icon={icon} color={color} size={size} />
			{text}
		</div>
	);
}

Marker.defaultProps = {
	icon: faMapPin,
	color: "red",
	size: "2x",
} as MarkerProps;
